import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import AuthImg from '../components/devreport/presentation/AuthImg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const AthleteMedia = () => {
  const { athleteId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [athlete, setAthlete] = useState(null);
  const [galleryStructure, setGalleryStructure] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('gallery');
  
  // Selected year/month for viewing
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  
  // Dialog states
  const [uploadPhotoDialog, setUploadPhotoDialog] = useState(false);
  const [bulkUploadDialog, setBulkUploadDialog] = useState(false);
  const [addHighlightDialog, setAddHighlightDialog] = useState(false);
  const [addPhotoLinkDialog, setAddPhotoLinkDialog] = useState(false);
  const [viewPhotoDialog, setViewPhotoDialog] = useState(false);
  const [viewHighlightDialog, setViewHighlightDialog] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const [editHighlightDialog, setEditHighlightDialog] = useState(false);
  
  // Form states
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear());
  const [uploadMonth, setUploadMonth] = useState(new Date().getMonth() + 1);
  const [uploadCaption, setUploadCaption] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Bulk upload states
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploadProgress, setBulkUploadProgress] = useState({ uploaded: 0, total: 0, results: [] });
  
  const [newHighlight, setNewHighlight] = useState({
    title: '',
    video_url: '',
    description: '',
    match_date: '',
    competition: '',
    opponent: ''
  });

  // Photo-link (external) form state
  const emptyPhotoLink = {
    external_url: '', thumbnail_url: '', caption: '',
    year: new Date().getFullYear(), month: new Date().getMonth() + 1,
  };
  const [photoLink, setPhotoLink] = useState(emptyPhotoLink);
  const [savingPhotoLink, setSavingPhotoLink] = useState(false);

  const canManage = ['Admin', 'Staff'].includes(user?.role);

  const fetchData = useCallback(async () => {
    if (!athleteId) return;
    
    setLoading(true);
    try {
      const [athleteRes, structureRes, highlightsRes] = await Promise.all([
        axios.get(`${API}/athletes/${athleteId}`),
        axios.get(`${API}/media/photos/${athleteId}/structure`),
        axios.get(`${API}/media/highlights/${athleteId}`)
      ]);
      
      setAthlete(athleteRes.data);
      setGalleryStructure(structureRes.data);
      setHighlights(highlightsRes.data);
      
      // If structure exists, select the first year/month
      if (structureRes.data.length > 0) {
        const firstYear = structureRes.data[0];
        setSelectedYear(firstYear.year);
        if (firstYear.months.length > 0) {
          setSelectedMonth(firstYear.months[0].month);
          // Fetch photos for this month
          const photosRes = await axios.get(`${API}/media/photos/${athleteId}?year=${firstYear.year}&month=${firstYear.months[0].month}`);
          setPhotos(photosRes.data);
        }
      }
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [athleteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchPhotosForMonth = async (year, month) => {
    try {
      const response = await axios.get(`${API}/media/photos/${athleteId}?year=${year}&month=${month}`);
      setPhotos(response.data);
      setSelectedYear(year);
      setSelectedMonth(month);
    } catch (error) {
      toast.error('Failed to load photos');
    }
  };

  const handleUploadPhoto = async () => {
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('athlete_id', athleteId);
    formData.append('year', uploadYear);
    formData.append('month', uploadMonth);
    if (uploadCaption) {
      formData.append('caption', uploadCaption);
    }
    
    try {
      await axios.post(`${API}/media/photos/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded successfully');
      setUploadPhotoDialog(false);
      setUploadFile(null);
      setUploadCaption('');
      fetchData();
      
      // Refresh photos if viewing the same month
      if (selectedYear === uploadYear && selectedMonth === uploadMonth) {
        fetchPhotosForMonth(uploadYear, uploadMonth);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      await axios.delete(`${API}/media/photos/${photoId}`);
      toast.success('Photo deleted');
      fetchData();
      if (selectedYear && selectedMonth) {
        fetchPhotosForMonth(selectedYear, selectedMonth);
      }
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleAddPhotoLink = async () => {
    const url = (photoLink.external_url || '').trim();
    if (!url || !/^https?:\/\//i.test(url)) {
      toast.error('Please enter a valid URL (https://…)');
      return;
    }
    if (photoLink.month < 1 || photoLink.month > 12) {
      toast.error('Month must be between 1 and 12');
      return;
    }
    setSavingPhotoLink(true);
    try {
      await axios.post(`${API}/media/photos/link`, {
        athlete_id: athleteId,
        year: photoLink.year,
        month: photoLink.month,
        external_url: url,
        thumbnail_url: photoLink.thumbnail_url?.trim() || null,
        caption: photoLink.caption?.trim() || null,
      });
      toast.success('External photo link added');
      setAddPhotoLinkDialog(false);
      setPhotoLink(emptyPhotoLink);
      fetchData();
      if (selectedYear && selectedMonth) {
        fetchPhotosForMonth(selectedYear, selectedMonth);
      } else {
        fetchPhotosForMonth(photoLink.year, photoLink.month);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add link');
    } finally {
      setSavingPhotoLink(false);
    }
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }
    
    if (bulkFiles.length > 20) {
      toast.error('Maximum 20 files per upload');
      return;
    }
    
    setUploading(true);
    setBulkUploadProgress({ uploaded: 0, total: bulkFiles.length, results: [] });
    
    const formData = new FormData();
    formData.append('athlete_id', athleteId);
    formData.append('year', uploadYear);
    formData.append('month', uploadMonth);
    
    for (const file of bulkFiles) {
      formData.append('files', file);
    }
    
    try {
      const response = await axios.post(`${API}/media/photos/upload-bulk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { uploaded, failed, total_uploaded, total_failed } = response.data;
      
      setBulkUploadProgress({
        uploaded: total_uploaded,
        total: bulkFiles.length,
        results: [...uploaded.map(u => ({ ...u, status: 'success' })), ...failed.map(f => ({ ...f, status: 'failed' }))]
      });
      
      if (total_failed === 0) {
        toast.success(`Successfully uploaded ${total_uploaded} photos!`);
      } else {
        toast.warning(`Uploaded ${total_uploaded} photos, ${total_failed} failed`);
      }
      
      // Refresh data
      fetchData();
      if (selectedYear === uploadYear && selectedMonth === uploadMonth) {
        fetchPhotosForMonth(uploadYear, uploadMonth);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const resetBulkUpload = () => {
    setBulkFiles([]);
    setBulkUploadProgress({ uploaded: 0, total: 0, results: [] });
    setBulkUploadDialog(false);
  };

  const handleAddHighlight = async () => {
    if (!newHighlight.title || !newHighlight.video_url) {
      toast.error('Please enter a title and video URL');
      return;
    }
    
    try {
      await axios.post(`${API}/media/highlights`, {
        ...newHighlight,
        athlete_id: athleteId
      });
      toast.success('Highlight added successfully');
      setAddHighlightDialog(false);
      setNewHighlight({
        title: '',
        video_url: '',
        description: '',
        match_date: '',
        competition: '',
        opponent: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add highlight');
    }
  };

  const handleUpdateHighlight = async () => {
    if (!selectedHighlight) return;
    
    try {
      await axios.put(`${API}/media/highlights/${selectedHighlight.id}`, {
        title: selectedHighlight.title,
        video_url: selectedHighlight.video_url,
        description: selectedHighlight.description,
        match_date: selectedHighlight.match_date,
        competition: selectedHighlight.competition,
        opponent: selectedHighlight.opponent
      });
      toast.success('Highlight updated');
      setEditHighlightDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update highlight');
    }
  };

  const handleDeleteHighlight = async (highlightId) => {
    if (!window.confirm('Are you sure you want to delete this highlight?')) return;
    
    try {
      await axios.delete(`${API}/media/highlights/${highlightId}`);
      toast.success('Highlight deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete highlight');
    }
  };

  const toggleFeature = async (kind, id, currentValue, e) => {
    if (e) e.stopPropagation();
    try {
      const path = kind === 'photo' ? `photos/${id}/feature` : `highlights/${id}/feature`;
      await axios.patch(`${API}/media/${path}?featured=${!currentValue}`);
      toast.success(!currentValue ? 'Added to Featured' : 'Removed from Featured');
      if (kind === 'photo') {
        if (selectedYear && selectedMonth) fetchPhotosForMonth(selectedYear, selectedMonth);
      } else {
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update feature status');
    }
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      youtube: '🎬',
      vimeo: '🎥',
      google_drive: '📁',
      veo: '⚽',
      other: '🔗'
    };
    return icons[platform] || '🔗';
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= currentYear - 5; y--) {
      years.push(y);
    }
    return years;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6 flex justify-between items-start">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
              ← Back
            </Button>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Media & Highlights
              </h2>
              <p className="text-gray-600 dark:text-gray-500 mt-1">
                {athlete?.full_name} - {athlete?.squad || 'No Squad'}
              </p>
            </div>
          </div>
          
          {canManage && (
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => setUploadPhotoDialog(true)}
                data-testid="upload-photo-btn"
              >
                📷 Upload Photo
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setBulkFiles([]);
                  setBulkUploadProgress({ uploaded: 0, total: 0, results: [] });
                  setBulkUploadDialog(true);
                }}
                data-testid="bulk-upload-btn"
              >
                📁 Bulk Upload
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setPhotoLink(emptyPhotoLink);
                  setAddPhotoLinkDialog(true);
                }}
                data-testid="add-photo-link-btn"
              >
                🔗 Add Link
              </Button>
              <Button 
                className="bg-pfap-green hover:bg-pfap-green/90"
                onClick={() => setAddHighlightDialog(true)}
                data-testid="add-highlight-btn"
              >
                🎬 Add Highlight
              </Button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="gallery" className="gap-2">
              📷 Training Gallery
            </TabsTrigger>
            <TabsTrigger value="highlights" className="gap-2">
              🎬 Match Highlights
            </TabsTrigger>
          </TabsList>

          {/* Training Gallery Tab */}
          <TabsContent value="gallery">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Year/Month Navigation */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-sm">Browse by Date</CardTitle>
                </CardHeader>
                <CardContent>
                  {galleryStructure.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-500 text-sm">No photos uploaded yet</p>
                  ) : (
                    <div className="space-y-4">
                      {galleryStructure.map(yearData => (
                        <div key={yearData.year}>
                          <div className="font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                            📅 {yearData.year}
                            <Badge variant="outline" className="text-xs">
                              {yearData.total_photos} photos
                            </Badge>
                          </div>
                          <div className="space-y-1 ml-4">
                            {yearData.months.map(monthData => (
                              <button
                                key={monthData.month}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                  selectedYear === yearData.year && selectedMonth === monthData.month
                                    ? 'bg-emerald-100 text-emerald-800 font-medium'
                                    : 'hover:bg-gray-100 dark:bg-gray-700'
                                }`}
                                onClick={() => fetchPhotosForMonth(yearData.year, monthData.month)}
                              >
                                {monthData.name}
                                <span className="text-gray-500 dark:text-gray-500 ml-2">({monthData.count})</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Photo Grid */}
              <Card className="lg:col-span-3">
                <CardHeader>
                  <CardTitle>
                    {selectedYear && selectedMonth 
                      ? `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`
                      : 'Select a month to view photos'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {photos.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500 dark:text-gray-500">
                        {selectedYear ? 'No photos in this month' : 'Select a month from the sidebar'}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {photos.map(photo => {
                        const isLink = photo.media_type === 'link';
                        const displayUrl = isLink ? (photo.thumbnail_url || null) : null;
                        const openUrl = isLink ? photo.external_url : (photo.file_url ? `${BACKEND_URL}${photo.file_url}` : null);
                        return (
                        <div 
                          key={photo.id}
                          className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 cursor-pointer"
                          onClick={() => {
                            setSelectedPhoto(photo);
                            setViewPhotoDialog(true);
                          }}
                          data-testid={`photo-${photo.id}`}
                        >
                          {displayUrl ? (
                            <img 
                              src={displayUrl}
                              alt={photo.caption || 'Training photo'}
                              loading="lazy"
                              className="w-full h-full object-cover transition-transform group-hover:scale-105"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : null}
                          {!isLink && photo.file_url && (
                            <AuthImg src={photo.file_url} alt={photo.caption || 'Training photo'}
                              className="w-full h-full object-cover transition-transform group-hover:scale-105"
                              testId={`photo-image-${photo.id}`} />
                          )}
                          {isLink && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-emerald-50 dark:from-blue-900/20 dark:to-emerald-900/20 text-gray-500">
                              <span className="text-3xl mb-1">🔗</span>
                              <span className="text-[10px] font-medium">External Link</span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                          {isLink && (
                            <span className="absolute top-1.5 left-1.5 text-[10px] px-1.5 py-0.5 rounded bg-blue-500 text-white font-medium">Link</span>
                          )}
                          {photo.is_featured && (
                            <span className="absolute top-1.5 right-10 text-[10px] px-1.5 py-0.5 rounded bg-amber-400 text-amber-900 font-semibold flex items-center gap-0.5" data-testid={`photo-featured-badge-${photo.id}`}>
                              ★ Featured
                            </span>
                          )}
                          {canManage && (
                            <button
                              className="absolute bottom-2 right-2 w-8 h-8 bg-white/90 hover:bg-amber-400 hover:text-white rounded-full opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center shadow text-amber-500"
                              onClick={(e) => toggleFeature('photo', photo.id, photo.is_featured, e)}
                              title={photo.is_featured ? 'Unfeature' : 'Feature'}
                              data-testid={`photo-feature-${photo.id}`}
                            >
                              <span className={photo.is_featured ? 'text-amber-500' : ''}>★</span>
                            </button>
                          )}
                          {isLink && openUrl && (
                            <a
                              href={openUrl}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="absolute bottom-2 left-2 text-[10px] px-2 py-1 rounded bg-white/90 text-gray-900 opacity-0 group-hover:opacity-100 transition-opacity font-medium hover:bg-white"
                              data-testid={`photo-open-${photo.id}`}
                            >
                              Open ↗
                            </a>
                          )}
                          {canManage && (
                            <button
                              className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePhoto(photo.id);
                              }}
                              data-testid={`photo-delete-${photo.id}`}
                            >
                              ×
                            </button>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Match Highlights Tab */}
          <TabsContent value="highlights">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {highlights.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center">
                    <p className="text-gray-500 dark:text-gray-500">No video highlights added yet</p>
                  </CardContent>
                </Card>
              ) : (
                highlights.map(highlight => (
                  <Card key={highlight.id} className="overflow-hidden">
                    <div 
                      className="aspect-video bg-gray-900 cursor-pointer relative group"
                      onClick={() => {
                        setSelectedHighlight(highlight);
                        setViewHighlightDialog(true);
                      }}
                    >
                      {highlight.thumbnail_url ? (
                        <img 
                          src={highlight.thumbnail_url}
                          alt={highlight.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-6xl text-gray-600 dark:text-gray-500">
                          {getPlatformIcon(highlight.platform)}
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 bg-white dark:bg-gray-800/90 rounded-full flex items-center justify-center shadow-lg opacity-80 group-hover:opacity-100 transition-opacity">
                          <span className="text-2xl ml-1">▶</span>
                        </div>
                      </div>
                      <Badge className="absolute top-2 right-2 bg-black/70">
                        {highlight.platform}
                      </Badge>
                      {highlight.is_featured && (
                        <Badge className="absolute top-2 left-2 bg-amber-400 text-amber-900 font-semibold" data-testid={`highlight-featured-badge-${highlight.id}`}>
                          ★ Featured
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1">{highlight.title}</h3>
                      {highlight.competition && (
                        <p className="text-sm text-gray-600 dark:text-gray-500">{highlight.competition}</p>
                      )}
                      {highlight.match_date && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{highlight.match_date}</p>
                      )}
                      {canManage && (
                        <div className="flex gap-2 mt-3">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => toggleFeature('highlight', highlight.id, highlight.is_featured, e)}
                            className={highlight.is_featured ? 'text-amber-600 border-amber-300' : ''}
                            data-testid={`highlight-feature-${highlight.id}`}
                          >
                            {highlight.is_featured ? '★ Unfeature' : '☆ Feature'}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedHighlight(highlight);
                              setEditHighlightDialog(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteHighlight(highlight.id);
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Upload Photo Dialog */}
        <Dialog open={uploadPhotoDialog} onOpenChange={setUploadPhotoDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Photo</DialogTitle>
              <DialogDescription>Add a photo to {athlete?.full_name}'s training gallery</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Year</Label>
                  <Select value={String(uploadYear)} onValueChange={v => setUploadYear(parseInt(v))}>
                    <SelectTrigger data-testid="upload-year">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {generateYearOptions().map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Month</Label>
                  <Select value={String(uploadMonth)} onValueChange={v => setUploadMonth(parseInt(v))}>
                    <SelectTrigger data-testid="upload-month">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTH_NAMES.map((name, idx) => (
                        <SelectItem key={idx + 1} value={String(idx + 1)}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Photo</Label>
                <Input 
                  type="file"
                  accept="image/*"
                  onChange={e => setUploadFile(e.target.files?.[0])}
                  data-testid="upload-file"
                />
              </div>
              
              <div>
                <Label>Caption (optional)</Label>
                <Input 
                  value={uploadCaption}
                  onChange={e => setUploadCaption(e.target.value)}
                  placeholder="Add a caption..."
                  data-testid="upload-caption"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadPhotoDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleUploadPhoto} 
                disabled={uploading}
                className="bg-pfap-green hover:bg-pfap-green/90"
              >
                {uploading ? 'Uploading...' : 'Upload Photo'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={bulkUploadDialog} onOpenChange={(open) => { if (!uploading) setBulkUploadDialog(open); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Bulk Photo Upload</DialogTitle>
              <DialogDescription>Upload multiple photos at once (max 20 files)</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Year</Label>
                  <Select value={String(uploadYear)} onValueChange={v => setUploadYear(parseInt(v))} disabled={uploading}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {generateYearOptions().map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Month</Label>
                  <Select value={String(uploadMonth)} onValueChange={v => setUploadMonth(parseInt(v))} disabled={uploading}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTH_NAMES.map((name, idx) => (
                        <SelectItem key={idx + 1} value={String(idx + 1)}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Select Photos</Label>
                <Input 
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={e => setBulkFiles(Array.from(e.target.files || []))}
                  disabled={uploading}
                  data-testid="bulk-upload-file"
                />
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  Supported formats: JPG, PNG, GIF, WebP (max 10MB each)
                </p>
              </div>
              
              {bulkFiles.length > 0 && !uploading && (
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {bulkFiles.length} file{bulkFiles.length !== 1 ? 's' : ''} selected:
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {bulkFiles.map((file, idx) => (
                      <div key={idx} className="text-xs text-gray-600 dark:text-gray-500 flex justify-between">
                        <span className="truncate max-w-[200px]">{file.name}</span>
                        <span className="text-gray-400 dark:text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {uploading && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Uploading...</span>
                    <span className="text-sm text-gray-500 dark:text-gray-500">
                      {bulkUploadProgress.uploaded} / {bulkUploadProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${bulkUploadProgress.total > 0 ? (bulkUploadProgress.uploaded / bulkUploadProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}
              
              {bulkUploadProgress.results.length > 0 && !uploading && (
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Upload Results:</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {bulkUploadProgress.results.map((result, idx) => (
                      <div 
                        key={idx} 
                        className={`text-xs flex items-center gap-2 ${result.status === 'success' ? 'text-emerald-600' : 'text-red-600'}`}
                      >
                        <span>{result.status === 'success' ? '✓' : '✗'}</span>
                        <span className="truncate">{result.filename}</span>
                        {result.error && <span className="text-gray-500 dark:text-gray-500">- {result.error}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              {bulkUploadProgress.results.length > 0 && !uploading ? (
                <Button onClick={resetBulkUpload} className="bg-pfap-green hover:bg-pfap-green/90">
                  Done
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setBulkUploadDialog(false)} disabled={uploading}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleBulkUpload} 
                    disabled={uploading || bulkFiles.length === 0}
                    className="bg-pfap-green hover:bg-pfap-green/90"
                  >
                    {uploading ? 'Uploading...' : `Upload ${bulkFiles.length} Photo${bulkFiles.length !== 1 ? 's' : ''}`}
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Add Photo Link Dialog — external (Drive/Imgur/etc.) */}
        <Dialog open={addPhotoLinkDialog} onOpenChange={setAddPhotoLinkDialog}>
          <DialogContent data-testid="add-photo-link-dialog">
            <DialogHeader>
              <DialogTitle>Add External Photo Link</DialogTitle>
              <DialogDescription>
                Link an image from Google Drive, Dropbox, or any public URL — nothing is stored on our servers.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label htmlFor="link-url">Image / Media URL *</Label>
                <Input
                  id="link-url"
                  placeholder="https://drive.google.com/..."
                  value={photoLink.external_url}
                  onChange={(e) => setPhotoLink({ ...photoLink, external_url: e.target.value })}
                  data-testid="photo-link-url"
                />
              </div>
              <div>
                <Label htmlFor="link-thumb">Thumbnail URL (optional)</Label>
                <Input
                  id="link-thumb"
                  placeholder="https://.../preview.jpg"
                  value={photoLink.thumbnail_url}
                  onChange={(e) => setPhotoLink({ ...photoLink, thumbnail_url: e.target.value })}
                  data-testid="photo-link-thumb"
                />
                <p className="text-[10px] text-gray-400 mt-1">If omitted, a generic Link icon is shown in the gallery.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="link-year">Year</Label>
                  <Input
                    id="link-year" type="number"
                    value={photoLink.year}
                    onChange={(e) => setPhotoLink({ ...photoLink, year: parseInt(e.target.value) || new Date().getFullYear() })}
                    data-testid="photo-link-year"
                  />
                </div>
                <div>
                  <Label htmlFor="link-month">Month (1–12)</Label>
                  <Input
                    id="link-month" type="number" min={1} max={12}
                    value={photoLink.month}
                    onChange={(e) => setPhotoLink({ ...photoLink, month: parseInt(e.target.value) || 1 })}
                    data-testid="photo-link-month"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="link-caption">Caption (optional)</Label>
                <Input
                  id="link-caption"
                  placeholder="e.g. Team photo after U18 derby"
                  value={photoLink.caption}
                  onChange={(e) => setPhotoLink({ ...photoLink, caption: e.target.value })}
                  data-testid="photo-link-caption"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddPhotoLinkDialog(false)} data-testid="photo-link-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleAddPhotoLink}
                disabled={savingPhotoLink}
                className="bg-pfap-green hover:bg-pfap-green/90"
                data-testid="photo-link-save"
              >
                {savingPhotoLink ? 'Saving…' : 'Add Link'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Highlight Dialog */}
        <Dialog open={addHighlightDialog} onOpenChange={setAddHighlightDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Video Highlight</DialogTitle>
              <DialogDescription>Add a match highlight video link</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input 
                  value={newHighlight.title}
                  onChange={e => setNewHighlight({...newHighlight, title: e.target.value})}
                  placeholder="e.g., Goal vs Benfica"
                  data-testid="highlight-title"
                />
              </div>
              
              <div>
                <Label>Video URL *</Label>
                <Input 
                  value={newHighlight.video_url}
                  onChange={e => setNewHighlight({...newHighlight, video_url: e.target.value})}
                  placeholder="YouTube, Vimeo, Google Drive, or Veo link"
                  data-testid="highlight-url"
                />
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  Supported: YouTube, Vimeo, Google Drive, Veo
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Match Date</Label>
                  <Input 
                    type="date"
                    value={newHighlight.match_date}
                    onChange={e => setNewHighlight({...newHighlight, match_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Competition</Label>
                  <Input 
                    value={newHighlight.competition}
                    onChange={e => setNewHighlight({...newHighlight, competition: e.target.value})}
                    placeholder="e.g., League Match"
                  />
                </div>
              </div>
              
              <div>
                <Label>Opponent</Label>
                <Input 
                  value={newHighlight.opponent}
                  onChange={e => setNewHighlight({...newHighlight, opponent: e.target.value})}
                  placeholder="e.g., SL Benfica"
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={newHighlight.description}
                  onChange={e => setNewHighlight({...newHighlight, description: e.target.value})}
                  placeholder="Brief description of the highlight..."
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddHighlightDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleAddHighlight}
                className="bg-pfap-green hover:bg-pfap-green/90"
              >
                Add Highlight
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Photo Dialog (Lightbox) */}
        <Dialog open={viewPhotoDialog} onOpenChange={setViewPhotoDialog}>
          <DialogContent className="max-w-4xl">
            {selectedPhoto && (
              <div>
                <AuthImg
                  src={selectedPhoto.file_url}
                  alt={selectedPhoto.caption || 'Training photo'}
                  className="w-full rounded-lg"
                  testId="photo-lightbox-image"
                />
                {selectedPhoto.caption && (
                  <p className="mt-4 text-gray-700 dark:text-gray-300">{selectedPhoto.caption}</p>
                )}
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                  Uploaded by {selectedPhoto.uploaded_by_name} • {new Date(selectedPhoto.uploaded_at).toLocaleDateString()}
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* View Highlight Dialog (Video Player) */}
        <Dialog open={viewHighlightDialog} onOpenChange={setViewHighlightDialog}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{selectedHighlight?.title}</DialogTitle>
              {selectedHighlight?.competition && (
                <DialogDescription>
                  {selectedHighlight.competition} {selectedHighlight.opponent && `vs ${selectedHighlight.opponent}`}
                </DialogDescription>
              )}
            </DialogHeader>
            
            {selectedHighlight && (
              <div className="space-y-4">
                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  {selectedHighlight.embed_url ? (
                    <iframe
                      src={selectedHighlight.embed_url}
                      className="w-full h-full"
                      allowFullScreen
                      allow="autoplay; encrypted-media"
                      title={selectedHighlight.title}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white">
                      <a 
                        href={selectedHighlight.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-400 hover:underline"
                      >
                        Open video in new tab →
                      </a>
                    </div>
                  )}
                </div>
                
                {selectedHighlight.description && (
                  <p className="text-gray-700 dark:text-gray-300">{selectedHighlight.description}</p>
                )}
                
                {selectedHighlight.match_date && (
                  <p className="text-sm text-gray-500 dark:text-gray-500">Date: {selectedHighlight.match_date}</p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Highlight Dialog */}
        <Dialog open={editHighlightDialog} onOpenChange={setEditHighlightDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Highlight</DialogTitle>
            </DialogHeader>
            
            {selectedHighlight && (
              <div className="space-y-4 py-4">
                <div>
                  <Label>Title</Label>
                  <Input 
                    value={selectedHighlight.title}
                    onChange={e => setSelectedHighlight({...selectedHighlight, title: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Video URL</Label>
                  <Input 
                    value={selectedHighlight.video_url}
                    onChange={e => setSelectedHighlight({...selectedHighlight, video_url: e.target.value})}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Match Date</Label>
                    <Input 
                      type="date"
                      value={selectedHighlight.match_date || ''}
                      onChange={e => setSelectedHighlight({...selectedHighlight, match_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Competition</Label>
                    <Input 
                      value={selectedHighlight.competition || ''}
                      onChange={e => setSelectedHighlight({...selectedHighlight, competition: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Opponent</Label>
                  <Input 
                    value={selectedHighlight.opponent || ''}
                    onChange={e => setSelectedHighlight({...selectedHighlight, opponent: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Description</Label>
                  <Textarea 
                    value={selectedHighlight.description || ''}
                    onChange={e => setSelectedHighlight({...selectedHighlight, description: e.target.value})}
                  />
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditHighlightDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleUpdateHighlight}
                className="bg-pfap-green hover:bg-pfap-green/90"
              >
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AthleteMedia;
